import { DataGridActionsColumn, TableView1 } from "..";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ConfirmNotification } from "../Dialogs";
import {
  useDeleteFeature,
  useFeaturesList,
  useSubscriptionsList,
  useUpdateSubscription,
} from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { Button, Stack } from "@mui/material";
import { useParams } from "react-router";
import { bool } from "prop-types";
import { enqueueSnackbar } from "notistack";
import { AddFeature } from "./AddFeature";
import { isEqual } from "lodash";

type FeaturesListProps = {
  showEdit?: boolean;
  showDelete?: boolean;
};

type FeatureRow = {
  id?: string;
  Id?: string;
  name?: string;
  key?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
  [key: string]: unknown;
};

type FeatureAction = "feature-edit" | "feature-delete";

type DeleteMultipleIds = Array<string | number>;

type DialogParameters = {
  action?: string;
  subscriptionFeatureId?: string;
  ids?: DeleteMultipleIds;
};

type ConfirmResultPayload = {
  result?: string;
  parameters?: DialogParameters;
};

type SelectionModel = Array<string>;

type ColumnValueGetterParams = {
  value?: string | number | Date | null;
};

export const FeaturesList = (props: FeaturesListProps) => {
  const { key } = useParams();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const { featuresList, loading: loadingList } = useFeaturesList({});
  const { deleteSubscriptionFeature, deleting } = useDeleteFeature();
  const { subscriptionsList } = useSubscriptionsList({
    planKey: key,
  });

  // const { updateRoleModules, loading: loadingUpdate } = useUpdateRoleModules();

  const { updateSubscriptionPlan, loading: loadingUpdate } =
    useUpdateSubscription(
      () => {
        // resetForm();
        // handleClose();
        enqueueSnackbar(gettext(" company has been added"), {
          variant: "success",
        });
      },
      (error) => {
        if (error === "409") {
          // setEmailError(true);
        } else if (error === "410") {
          enqueueSnackbar(
            gettext("You are not authorized to perform this action"),
            {
              variant: "error",
            },
          );
        } else {
          enqueueSnackbar(gettext("Error: Something went wrong!"), {
            variant: "error",
          });
        }
      },
    );

  //#region useState
  const [rows, setRows] = useState<FeatureRow[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<DialogParameters>(
    {},
  );
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<FeatureRow>({});
  const [selectedIds, setSelectedIds] = useState<SelectionModel>(
    subscriptionsList[0]?.features || [],
  );
  const [roleName, setRoleName] = useState("");
  const [subscriptionId, setSubscriptionId] = useState(
    "680e5af61169d5c9d8e37cee",
  );
  // const [disabledSaveButton, setDisabledSaveButton] = useState(true);
  //#endregion useState

  //region React Hook (useEffect)
  useEffect(() => {
    const currentSub =
      subscriptionsList.find((s) => s.key === key || s.Id === key) ||
      subscriptionsList[0];
    if (currentSub?.features && key) {
      setSelectedIds(currentSub.features);
      setRoleName(currentSub.name || "");
      if (key === currentSub.key) {
        setSubscriptionId(currentSub.Id || "");
      }
    }
  }, [key, subscriptionsList]);

  useEffect(() => {
    if (!isEqual(rows, featuresList)) {
      // Use setTimeout to defer state updates and avoid cascading renders
      setTimeout(() => {
        setRows(featuresList);
      }, 0);
    }
  }, [featuresList, rows]);

  //endregion React Hook (useEffect)

  //#region useMemo

  const showItems = useMemo(() => {
    return {
      showEdit: props.showEdit ?? false,
      showDelete: props.showDelete ?? false,
    };
  }, [props.showEdit, props.showDelete]);

  const showActionsColumn = useMemo(() => {
    return Object.values(showItems).some((item) => item === true);
  }, [showItems]);

  //#endregion useMemo

  //#region Handlers

  const menuClick = (action: FeatureAction, row: FeatureRow) => {
    switch (action) {
      case "feature-edit":
        handleEdit(row);
        break;
      case "feature-delete":
        handleOnClickDeleteSingle(row);
        break;
      default:
        break;
    }
  };

  //#region Table Data
  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        code: !key,
        desc: true,
        createdAt: false,
        updatedAt: false,
        actions: !!props.showEdit || !!props.showDelete,
      },
    },
  };
  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
    },
    {
      field: "name",
      headerName: gettext("Feature"),
      flex: 1,
      minWidth: 250,
      headerAlign: "left",
      type: "string",
    },
    {
      field: "key",
      headerName: gettext("Key"),
      flex: 1,
      minWidth: 200,
      headerAlign: "left",
      type: "string",
    },
    // {
    //   field: "desc",
    //   headerName: gettext("Description"),
    //   flex: 1,
    //   // minWidth: 150,
    //   headerAlign: "left",
    //   type: "string"
    // },
    {
      field: "createdAt",
      headerName: gettext("Created At"),
      headerAlign: "left",
      type: "date",
      valueGetter: ({ value }: ColumnValueGetterParams) =>
        value && new Date(value),
    },
    {
      field: "updatedAt",
      headerName: gettext("Updated At"),
      headerAlign: "left",
      type: "date",
      valueGetter: ({ value }: ColumnValueGetterParams) =>
        value && new Date(value),
    },
    ...DataGridActionsColumn(
      showActionsColumn,
      "feature",
      showItems,
      menuClick,
    ),
  ];
  //#endregion Table Data

  //#region Handlers (continued)

  const handleEdit = (row: FeatureRow) => {
    setEditRow(row);
    setAddFormOpen(true);
  };

  const handleOnClickDeleteSingle = (row: FeatureRow) => {
    setDialogParameters({
      action: "delete-single",
      subscriptionFeatureId: row.Id || row.id,
    });
    setShowConfirm(true);
  };

  const handleOnClickDeleteMultiple = (ids: DeleteMultipleIds) => {
    setDialogParameters({
      action: "delete-multi",
      ids: ids,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: ConfirmResultPayload) => {
    if (obj.result === "yes") {
      deleteSubscriptionFeature({
        subscriptionFeatureId: obj?.parameters?.subscriptionFeatureId,
      });
    }
    setShowConfirm(false);
  };

  const SaveFeatures = () => {
    const variables = {
      features: selectedIds,
      subscriptionPlanId: subscriptionId,
    };
    updateSubscriptionPlan({
      variables: variables,
      // refetchQueries: ["getAllRoles"]
    }).then();
  };

  const handleOnRowSelectionModelChange = (ids: SelectionModel) => {
    setSelectedIds(ids);
    // if (ids.length) {
    //   setDisabledSaveButton(false);
    // } else {
    //   setDisabledSaveButton(true);
    // }
  };

  const postToolbar = () => {
    return (
      <>
        <Stack direction="row" spacing={1} sx={{ pl: 1, alignItems: "center" }}>
          {!key && (
            <Button
              variant="contained"
              onClick={() => {
                setAddFormOpen(true);
              }}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {gettext("Add feature")}
            </Button>
          )}
          {!!key && (
            <Button
              variant="contained"
              // disabled={disabledSaveButton}
              onClick={() => {
                SaveFeatures();
              }}
            >
              {gettext("Save")}
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const handleCloseAddForm = (event: boolean) => {
    setAddFormOpen(event);
    setEditRow({});
  };
  //#endregion Handlers

  return (
    <>
      <TableView1
        title={
          roleName ? gettext("Features for ") + roleName : gettext("Features")
        }
        rows={rows}
        // rows={[]}
        columns={columns}
        loading={loadingList || loadingUpdate}
        checkboxSelection={!!key}
        initialState={initialState}
        // preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        // headerRight={headerRight()}
        showGridToolbar={true}
        showGridToolbarDeleteButton={false}
        showGridToolbarDensitySelector={false}
        showGridToolbarColumnsButton={false}
        showGridToolbarQuickFilter
        showGridToolbarFilterButton={false}
        showGridToolbarExport={false}
        onRowSelectionModelChange={handleOnRowSelectionModelChange}
        onClickDeleteMultiple={handleOnClickDeleteMultiple}
        selectedIds={key ? selectedIds : []}
      />
      <AddFeature
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
      />
      {deleting ? (
        <div className="position-absolute bg-dark opacity-50 w-100 h-100 d-flex justify-content-center align-items-center">
          {/*<Spinner animation="border" variant="light"/>*/}
        </div>
      ) : null}
    </>
  );
};

FeaturesList.propTypes = {
  showEdit: bool,
  showDelete: bool,
};
